import React, { useState } from "react";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { localToGMT } from "../utils/gmtUtils";
import AnimatedModal from "./ui/AnimatedModal";
import { Button } from "./ui";

const SendConfirmationModal = ({ isOpen, onClose, onConfirm }) => {
  const [selectedOption, setSelectedOption] = useState("day");
  const [customDate, setCustomDate] = useState(new Date());

  const getExpiryDate = () => {
    const now = new Date();
    switch (selectedOption) {
      case "day":
        return new Date(now.setDate(now.getDate() + 1));
      case "twoDays":
        return new Date(now.setDate(now.getDate() + 2));
      case "week":
        return new Date(now.setDate(now.getDate() + 7));
      case "month":
        return new Date(now.setMonth(now.getMonth() + 1));
      case "custom":
        return customDate;
      default:
        return new Date(now.setDate(now.getDate() + 1));
    }
  };

  const handleConfirm = () => {
    // Convert local date to GMT before sending to server
    const localDate = getExpiryDate();
    const gmtDate = localToGMT(localDate);
    onConfirm(gmtDate);
    onClose();
  };

  const options = [
    { value: "day", label: "A day from now" },
    { value: "twoDays", label: "Two days from now" },
    { value: "week", label: "A week from now" },
    { value: "month", label: "A month from now" },
    { value: "custom", label: "Custom date" },
  ];

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdrop
      zIndex="z-[9999]"
      contentClassName="bg-white overflow-hidden shadow-xl max-w-lg w-full"
    >
      <div className="px-4 pt-5 pb-4 sm:p-6">
        <div className="sm:flex sm:items-start">
          <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
            <PaperAirplaneIcon className="h-6 w-6 text-blue-600" />
          </div>
          <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
            <h3 className="text-lg font-medium text-gray-900">
              Send Document
            </h3>
            <div className="mt-2">
              <p className="text-sm text-gray-500">
                Please select when this document should expire:
              </p>
              <div className="mt-4">
                <div className="flex flex-wrap gap-2">
                  {options.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setSelectedOption(option.value)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                        selectedOption === option.value
                          ? "bg-primary text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              {selectedOption === "custom" && (
                <div className="mt-4">
                  <DatePicker
                    selected={customDate}
                    onChange={(date) => setCustomDate(date)}
                    showTimeSelect
                    dateFormat="MMMM d, yyyy h:mm aa"
                    minDate={new Date()}
                    minTime={new Date()}
                    maxTime={new Date(new Date().setHours(23, 59, 59))}
                    inline
                    className="border-1"
                    calendarClassName="bg-white rounded-lg shadow-lg"
                    dayClassName={() =>
                      "text-gray-700 hover:bg-primary hover:text-white rounded-full"
                    }
                    timeClassName={() =>
                      "text-gray-700 hover:bg-primary hover:text-white rounded"
                    }
                    wrapperClassName="w-full"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse sm:gap-3">
        <Button
          type="button"
          size="default"
          leftIcon={PaperAirplaneIcon}
          onClick={handleConfirm}
        >
          Send
        </Button>
        <Button
          type="button"
          variant="outline"
          size="default"
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </AnimatedModal>
  );
};

export default SendConfirmationModal;
